import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import {
  useBasicDesignChunks,
  useBasicDesignExpansionHistory,
} from '../../../hooks/useBasicDesignExpansion.js';
import type { BasicDesignChunkItem } from '../../../lib/api/basic-design-expansion.js';
import { ChildTicketPlanTable } from './ChildTicketPlanTable.js';
import { ChildTicketExpansionHistory } from './ChildTicketExpansionHistory.js';
import { ChildTicketExpandFlow, type ExpandMode } from './ChildTicketExpandFlow.js';
import { ChildTicketChunkBoard, type ChunkOpenOrigin } from './ChildTicketChunkBoard.js';
import {
  CHILD_TICKET_GROUPS,
  findChildTicketKind,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import { childTicketToneStyle } from './ChildTicketKindLabel.js';
import {
  addChildTicketChunk,
  alignChunksWithLedger,
  createChildTicketChunk,
  findChildTicketChunk,
  moveChildTicketChunk,
  removeChildTicketChunk,
  renameChildTicketChunk,
  reserveChildTicketChunkIds,
  writeChildTicketChunkRows,
  type ChildTicketChunk,
} from './child-ticket-chunks.js';
import {
  createInitialChildTicketRows,
  type BasicDesignPeople,
  duplicateChildTicketRow,
  insertChildTicketRow,
  moveChildTicketRow,
  removeChildTicketRow,
  reserveChildTicketRowIds,
  type ChildTicketRow,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import {
  isOverChildTicketPointLimit,
  totalResolvedPoint,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';

/**
 * 行間と表末尾の「＋」で入る種別。案件ごとに枚数が増えるのは Develop（リポジトリ別に割る）
 * なので、追加行はそこから書き始められるようにする。
 * @graph-connects none
 */
const ADDED_ROW_KIND_ID = 'develop';

/**
 * 台帳が読めていないときに使う空の台帳。描画のたびに新しい配列を作ると、それを見ている
 * `startReEdit` も作り直されるので、同じものを指し続ける。
 * @graph-connects none
 */
const NO_CHUNK_LEDGER: readonly BasicDesignChunkItem[] = [];

/**
 * シートが包みへ縮んでいく時間 (ms)。CSS の `child-chunk-fold` と合わせる。
 * 動きが終わってから一覧へ切り替えるので、両方で同じ値を持つ必要がある。
 * @graph-connects none
 */
const CHUNK_FOLD_MS = 560;

/**
 * 動きを減らす設定かどうか。畳む動きを出さない環境では待たずに切り替える
 * （待つだけの無反応な時間になるため）。
 * @graph-connects none
 */
function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 子票の枚数。ラベル文に連結せず、数字だけの小さな札として見出しの外に置く
 * @graph-connects none
 */
function CountBadge({ value }: { value: number }): ReactNode {
  return (
    <span className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] tabular-nums text-[var(--color-text-muted)]">
      {value}
    </span>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 合計 pt を数値主役で出す。ラベル行を上に積まず、単位を値の隣に小さく添える。上限を超えたら数字を赤へ振って、分けて進める合図を出す
 * @graph-connects isOverChildTicketPointLimit [calls] 上限超過の判定
 */
function PointTotal({ value, unit }: { value: number; unit: string }): ReactNode {
  const over = isOverChildTicketPointLimit(value);
  return (
    <p className="flex items-baseline gap-1.5">
      <span
        className="font-mono text-base font-semibold tabular-nums"
        style={{ color: over ? 'var(--color-error)' : 'var(--color-text-primary)' }}
      >
        {value}
      </span>
      <span className="text-xs text-[var(--color-text-muted)]">{unit}</span>
    </p>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件詳細ページ「基本設計」タブの本体。Backlog へ展開する子票群を表で組み立てる画面で、初期セット 13 行を起点に種別 / 項目 / リポジトリ / 内容 / pt / 担当者を編集する。シートはそのまま「チャンク」として切り出せ、切り出した先は包みの一覧として見渡し・追加・改名・削除ができる
 * @graph-connects ChildTicketPlanTable [calls] 子票行の編集表
 * @graph-connects ChildTicketChunkBoard [calls] 切り出したチャンクの一覧
 * @graph-connects createInitialChildTicketRows [calls] 初期セット 13 行の生成
 * @graph-connects createChildTicketChunk [calls] シートからのチャンクの切り出し
 * @graph-connects totalResolvedPoint [calls] 自動計算ぶんを含む合計 pt
 * @graph-connects insertChildTicketRow [calls] 行間・末尾への行の差し込み
 * @graph-connects PointTotal [calls] 合計 pt の表示
 */
export function ChildTicketPlanView({
  pjKey,
  caseTitle,
  people,
  isDevLeader,
}: {
  /** 母票の課題キー。展開先になる。 */
  pjKey: string;
  /** 母票の案件名。見出しとチャンク名に使う。 */
  caseTitle: string;
  /** 母票から取れる人（担当者の既定値に使う）。 */
  people: BasicDesignPeople;
  /** 今見ている人が母票の開発リーダーか。 */
  isDevLeader: boolean;
}): ReactNode {
  const { t } = useTranslation();
  // 展開前の確認ダイアログ（pt 超過 → 開発リーダーの引き受け → 予定日の入力）
  // 展開の確認ダイアログ。シート 1 枚を展開するのか、チャンクに分けて展開するのかで
  // 押したあとの行き先が変わるので、開いているかではなくどちらで開いたかを持つ
  const [expandMode, setExpandMode] = useState<ExpandMode | null>(null);
  const [expandNotice, setExpandNotice] = useState<string | null>(null);
  const chunkLedgerQuery = useBasicDesignChunks(pjKey);
  const chunkLedger = chunkLedgerQuery.data ?? NO_CHUNK_LEDGER;
  // 展開済みチャンクは消せない。Backlog に母票・中継チケット・子票が既にあるため
  const expandedChunkIds = new Set(chunkLedger.map((item) => item.chunkId));
  const historyQuery = useBasicDesignExpansionHistory(pjKey);
  const entries = historyQuery.data ?? [];
  // 一度展開したらシートは履歴として固定する。書き換えは「再編集」を通してだけ許す
  // （画面を開くたびに初期セットが出ると、展開済みの設計が無かったことになる）
  const [editing, setEditing] = useState(false);
  // チャンクに切り出す前のシート。切り出すと中身はチャンク側へ移る。
  const [draftRows, setDraftRows] = useState<ChildTicketRow[]>(() =>
    createInitialChildTicketRows(people),
  );
  const [chunks, setChunks] = useState<ChildTicketChunk[]>([]);
  // 今開いているチャンク。チャンクがあるのに null なら一覧（ボード）を見ている。
  const [openChunkId, setOpenChunkId] = useState<string | null>(null);
  const [folding, setFolding] = useState(false);
  const foldTimer = useRef<number | null>(null);
  // 包みを開いたときに、そこから広がって見せるための起点（押された包みの位置）
  const [openOrigin, setOpenOrigin] = useState<ChunkOpenOrigin | null>(null);
  const [unfolding, setUnfolding] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);

  // 展開済みチャンクは常に画面に出ている状態を保つ。再編集に入った時だけ組み直すと、
  // 履歴が無い案件（爺票の履歴の保存に失敗した回など）や編集中の読み直しで、Backlog に
  // あるチャンクが画面から消え、人はそれを「無い」と思って編集してしまう
  useEffect(() => {
    if (!chunkLedgerQuery.isSuccess) return;
    reserveChildTicketChunkIds(chunkLedger);
    reserveChildTicketRowIds(chunkLedger.flatMap((item) => item.rows));
    setChunks((prev) => alignChunksWithLedger(prev, chunkLedger));
  }, [chunkLedgerQuery.isSuccess, chunkLedger]);

  const chunked = chunks.length > 0;
  /** 全チャンクの合計 pt。チャンク一覧の見出しと、展開前の確認に使う。 */
  const chunkTotalPoint = chunks.reduce((sum, chunk) => sum + totalResolvedPoint(chunk.rows), 0);
  const openChunk = findChildTicketChunk(chunks, openChunkId);
  const onBoard = chunked && openChunk === undefined;
  const rows = chunked ? (openChunk?.rows ?? []) : draftRows;

  /** 今見ているシートの行を書き換える（下書きでもチャンクでも同じ口で受ける）。 */
  const updateRows = useCallback(
    (update: (prev: ChildTicketRow[]) => ChildTicketRow[]): void => {
      if (openChunkId === null) {
        setDraftRows(update);
        return;
      }
      setChunks((prev) => {
        const target = findChildTicketChunk(prev, openChunkId);
        if (target === undefined) return prev;
        return writeChildTicketChunkRows(prev, openChunkId, update(target.rows));
      });
    },
    [openChunkId],
  );

  const changeRow = useCallback(
    (rowId: string, patch: Partial<ChildTicketRow>): void => {
      updateRows((prev) => prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
    },
    [updateRows],
  );
  const duplicateRow = useCallback(
    (rowId: string): void => {
      updateRows((prev) => duplicateChildTicketRow(prev, rowId));
    },
    [updateRows],
  );
  const removeRow = useCallback(
    (rowId: string): void => {
      updateRows((prev) => removeChildTicketRow(prev, rowId));
    },
    [updateRows],
  );
  const moveRow = useCallback(
    (from: number, to: number): void => {
      updateRows((prev) => moveChildTicketRow(prev, from, to));
    },
    [updateRows],
  );
  const insertRow = useCallback(
    (index: number): void => {
      updateRows((prev) => insertChildTicketRow(prev, index, ADDED_ROW_KIND_ID));
    },
    [updateRows],
  );
  const replaceRows = useCallback(
    (next: ChildTicketRow[]): void => {
      updateRows(() => next);
    },
    [updateRows],
  );

  /**
   * 直近の展開を編集できる状態へ戻す。
   *
   * チャンクに分けて展開した案件は、シート 1 枚ではなく**台帳のチャンクをそのまま**組み直す。
   * チャンクを足すには展開済みのチャンクを全部送り直す必要があり（欠けていると API が拒む）、
   * 組み直さないとチャンクの追加そのものができない。
   *
   * 行 id とチャンク id は展開の差分・母票の突き合わせ鍵なので**そのまま使う**。採番は
   * 読み込み直後に 1 から数え直すため、先に追い越させてから編集に入る（でないと足した行 /
   * チャンクが既存のものと同じ id を持ち、1 枚の子票に 2 行が畳まれたり、既存の母票を
   * 別の設計で乗っ取る）。
   */
  const startReEdit = useCallback((): void => {
    const latest = entries[0];
    if (latest === undefined) return;
    // チャンクに分けた案件は、台帳と揃えた `chunks` がそのまま編集対象。ここで触らない
    // （空にすると、展開済みチャンクが画面から消えて平らなシートで上書きしてしまう）
    if (chunkLedger.length > 0) {
      setDraftRows(createInitialChildTicketRows(people));
      // 一覧から始める。どのチャンクを触るかは人に選ばせる
      setOpenChunkId(null);
      setEditing(true);
      return;
    }
    reserveChildTicketRowIds(latest.rows);
    // 行を残すようになる前の記録には行が無い。その回は初期セットから書き直す
    setDraftRows(
      latest.rows.length === 0 ? createInitialChildTicketRows(people) : [...latest.rows],
    );
    setChunks([]);
    setOpenChunkId(null);
    setEditing(true);
  }, [entries, people, chunkLedger]);

  /** 今のシートを包んでチャンクにし、動きが収まってから一覧へ移る。 */
  const foldIntoChunk = useCallback((): void => {
    if (folding) return;
    const commit = (): void => {
      setChunks((prev) =>
        addChildTicketChunk(prev, createChildTicketChunk(caseTitle, prev.length + 1, rows)),
      );
      setOpenChunkId(null);
      setFolding(false);
      setDraftRows(createInitialChildTicketRows(people));
    };
    if (prefersReducedMotion()) {
      commit();
      return;
    }
    setFolding(true);
    foldTimer.current = window.setTimeout(commit, CHUNK_FOLD_MS);
  }, [folding, caseTitle, rows, people]);

  // 包みの位置からシートが広がるように見せる（開く前に測った包みの矩形と、
  // 出きったシートの矩形の差を、そのまま動きの始点にする）。描画後・ペイント前に
  // 測る必要があるので layout effect で持つ。
  useLayoutEffect(() => {
    const origin = openOrigin;
    const sheet = sheetRef.current;
    if (origin === null || sheet === null) return;
    setOpenOrigin(null);
    if (prefersReducedMotion()) return;
    const rect = sheet.getBoundingClientRect();
    if (rect.width === 0 || origin.width === 0) return;
    sheet.style.setProperty('--chunk-from-x', `${String(origin.left - rect.left)}px`);
    sheet.style.setProperty('--chunk-from-y', `${String(origin.top - rect.top)}px`);
    sheet.style.setProperty('--chunk-from-scale', String(origin.width / rect.width));
    setUnfolding(true);
  }, [openOrigin]);

  const title = openChunk?.title ?? caseTitle;

  // 履歴とチャンク台帳が読めるまではシートを出さない。先にシートを出すと、展開済みの案件でも
  // 一瞬だけ初期セットが見えて「履歴が消えた」ように読める。台帳を待つのは、読めていない間に
  // チャンクを足すと採番が既存チャンクと衝突し、展開で既存の母票を乗っ取るため
  if (historyQuery.isPending || chunkLedgerQuery.isPending) {
    return (
      <div className="animate-content-reveal space-y-3">
        <Skeleton variant="rectangular" height={36} />
        <Skeleton variant="rectangular" height={420} />
      </div>
    );
  }

  const showHistory = entries.length > 0 && !editing;
  // チャンクに分けた回なのに台帳が読めていないと、組み直せず平らなシートで上書きしてしまう
  const chunkedLatest = (entries[0]?.chunks ?? []).length > 0;
  const reEditBlockedReason =
    chunkedLatest && chunkLedger.length === 0
      ? t('childTicketPlan.history.chunkLedgerMissing')
      : null;

  /** 展開の一連の流れ（確認 → 待機 → 結果）。どちらの画面でも出す。 */
  const overlays = (
    <ChildTicketExpandFlow
      pjKey={pjKey}
      mode={expandMode}
      rows={rows}
      chunks={chunks}
      people={people}
      isDevLeader={isDevLeader}
      onClose={() => {
        setExpandMode(null);
      }}
      onCancelled={() => {
        setExpandMode(null);
        setExpandNotice(t('childTicketPlan.expand.cancelled'));
      }}
      onExpanded={() => {
        setExpandMode(null);
        setExpandNotice(null);
        // 展開し終えた設計は履歴側で見せる（編集はここで閉じる）
        setEditing(false);
      }}
    />
  );

  if (showHistory) {
    return (
      <section className="animate-content-reveal space-y-3">
        <h2 className="truncate text-center text-base font-semibold text-[var(--color-text-primary)]">
          {caseTitle}
        </h2>
        <ChildTicketExpansionHistory
          entries={entries}
          onReEdit={startReEdit}
          reEditBlockedReason={reEditBlockedReason}
        />
        {overlays}
      </section>
    );
  }

  return (
    <section className="animate-content-reveal space-y-3">
      {/* 左 = チャンクの操作、中央 = 見出し、右 = 内訳と枚数とリセット。
          中央を本当に中央へ置きたいので、左右を同じ幅（1fr）で受ける */}
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-4">
        <div className="flex items-center gap-2 sm:justify-self-start">
          {/* 見出し脇のボタンは `.child-sheet-button` で寸法を共有する。
              色だけ data-tone で分ける（チャンクの操作 = 黄色、リセット = 地の色） */}
          {/* 再編集に入ったあとで思い直せるように、履歴へ戻る道を残す（まだ展開していないので
              ここで抜けても Backlog は変わらない） */}
          {editing && entries.length > 0 ? (
            <button
              type="button"
              data-tone="neutral"
              className="child-sheet-button"
              onClick={() => {
                setEditing(false);
                setExpandNotice(null);
              }}
            >
              {t('childTicketPlan.history.back')}
            </button>
          ) : null}
          {onBoard ? null : chunked ? (
            <button
              type="button"
              data-tone="chunk"
              className="child-sheet-button"
              onClick={() => {
                setOpenChunkId(null);
              }}
            >
              {t('childTicketPlan.chunks.back')}
            </button>
          ) : (
            <button
              type="button"
              data-tone="chunk"
              className="child-sheet-button"
              onClick={foldIntoChunk}
            >
              {t('childTicketPlan.actions.chunk')}
            </button>
          )}
        </div>

        {/* チャンクを開いている間は、見出しを黄色の帯にして一覧の包みと地続きに見せる */}
        <h2
          className={
            openChunk === undefined
              ? 'truncate text-base font-semibold text-[var(--color-text-primary)] sm:justify-self-center'
              : 'child-chunk-titlebar max-w-full truncate text-base font-semibold sm:justify-self-center'
          }
        >
          {title}
        </h2>

        <div className="flex flex-wrap items-center gap-2 sm:justify-self-end">
          {onBoard ? (
            <>
              <CountBadge value={chunks.length} />
              <PointTotal value={chunkTotalPoint} unit={t('childTicketPlan.columns.point')} />
              <button
                type="button"
                data-tone="chunk"
                className="child-sheet-button"
                disabled={chunks.length === 0}
                onClick={() => {
                  setExpandNotice(null);
                  setExpandMode('chunks');
                }}
              >
                {t('childTicketPlan.actions.expandChunks')}
              </button>
            </>
          ) : (
            <>
              {/* 総数は群の内訳より先に読ませたいので、札の並びの先頭に置く */}
              <CountBadge value={rows.length} />
              <div className="flex flex-wrap items-center gap-1.5">
                {CHILD_TICKET_GROUPS.map((group) => (
                  <span
                    key={group.id}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    style={childTicketToneStyle(group.tone)}
                  >
                    {t(group.labelKey)}
                    <span className="tabular-nums opacity-80">
                      {
                        rows.filter((row) => findChildTicketKind(row.kindId)?.group === group.id)
                          .length
                      }
                    </span>
                  </span>
                ))}
              </div>
              <button
                type="button"
                data-tone="neutral"
                className="child-sheet-button"
                onClick={() => {
                  replaceRows(createInitialChildTicketRows(people));
                }}
              >
                {t('childTicketPlan.actions.reset')}
              </button>
            </>
          )}
        </div>
      </div>

      {onBoard ? (
        <ChildTicketChunkBoard
          chunks={chunks}
          expandedChunkIds={expandedChunkIds}
          onOpen={(chunkId, origin) => {
            setOpenChunkId(chunkId);
            setOpenOrigin(origin);
          }}
          onRename={(chunkId, next) => {
            setChunks((prev) => renameChildTicketChunk(prev, chunkId, next));
          }}
          onRemove={(chunkId) => {
            setChunks((prev) => removeChildTicketChunk(prev, chunkId));
          }}
          onMove={(from, to) => {
            setChunks((prev) => moveChildTicketChunk(prev, from, to));
          }}
          onAdd={() => {
            const chunk = createChildTicketChunk(caseTitle, chunks.length + 1, undefined, people);
            setChunks((prev) => addChildTicketChunk(prev, chunk));
            setOpenChunkId(chunk.chunkId);
          }}
        />
      ) : (
        <>
          {/* 包むときは黄色の枠で囲ってから縮める。何がひとかたまりになるのかが先に見える。
              チャンクを開いている間は、その枠を出したままにして「包みの中に居る」と分かるようにする */}
          <div
            ref={sheetRef}
            onAnimationEnd={() => {
              setUnfolding(false);
            }}
            className={[
              openChunk === undefined ? '' : 'child-chunk-frame',
              folding ? 'child-chunk-folding' : '',
              unfolding ? 'child-chunk-unfolding' : '',
            ]
              .filter((name) => name !== '')
              .join(' ')}
          >
            {rows.length === 0 ? (
              <button
                type="button"
                onClick={() => {
                  insertRow(0);
                }}
                className="w-full cursor-pointer rounded-2xl border border-dashed border-[var(--color-border)] px-4 py-10 text-center text-sm text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-accent-primary)] hover:text-[var(--color-accent-primary)]"
              >
                {t('childTicketPlan.empty')}
              </button>
            ) : (
              <ChildTicketPlanTable
                rows={rows}
                onChangeRow={changeRow}
                onDuplicateRow={duplicateRow}
                onRemoveRow={removeRow}
                onMoveRow={moveRow}
                onInsertRow={insertRow}
                onReplaceRows={replaceRows}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <p className="text-[11px] text-[var(--color-text-muted)]">
              {t('childTicketPlan.sheetHint')}
            </p>
            <div className="ml-auto flex items-center gap-3">
              {/* 「展開」がまだ Backlog に書かないことは画面を見ても分からないので、ここだけ文で残す */}
              <span className="text-[11px] text-[var(--color-text-muted)]">
                {expandNotice ?? t('childTicketPlan.expandNote')}
              </span>
              <PointTotal
                value={totalResolvedPoint(rows)}
                unit={t('childTicketPlan.columns.point')}
              />
              {/* チャンクに分けている間は、シートを開いていてもチャンク展開を通す。
                  ここで平らな展開を走らせると、そのチャンクの子票が母票ではなく爺票の下に
                  ばらばらに作られ、チャンクの構造（母票・中継チケット）が出来ない */}
              <Button
                variant="primary"
                size="sm"
                disabled={rows.length === 0}
                onClick={() => {
                  setExpandNotice(null);
                  setExpandMode(chunked ? 'chunks' : 'sheet');
                }}
              >
                {t(
                  chunked
                    ? 'childTicketPlan.actions.expandChunks'
                    : 'childTicketPlan.actions.expand',
                )}
              </Button>
            </div>
          </div>
        </>
      )}

      {overlays}
    </section>
  );
}
